/**
 * Reading and changing exceptions, through the callables.
 *
 * One list, one detail, one create, one update, and one mutation for the
 * dozen status actions, keyed by callable name — they all take the id plus
 * a small payload and are followed by the same refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { toIsoDateTime } from '../../shared/dates';
import {
  toChainRole, toOutcome, toRisk, toStatus, toType,
  type ApprovalStep, type ChainRole, type ExceptionEvent, type ExceptionFields,
  type ExceptionRecord, type ExceptionReview, type ExceptionVersion, type ReviewOutcome, type RiskLevel, toReason } from './types';

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function toStep(s: Record<string, unknown>): ApprovalStep {
  return {
    stepOrder: num(s.stepOrder) ?? 0,
    approverUid: str(s.approverUid) ?? '',
    approverEmail: str(s.approverEmail),
    approverDisplayName: str(s.approverDisplayName),
    role: toChainRole(s.role),
    decision: s.decision === 'approved' || s.decision === 'rejected' ? s.decision : null,
    decidedAt: str(s.decidedAt),
    comments: str(s.comments),
  };
}

export function toRecord(d: Record<string, unknown>): ExceptionRecord {
  return {
    id: String(d.id ?? ''),
    title: str(d.title) ?? '(untitled)',
    description: str(d.description) ?? '',
    exceptionType: toType(d.exceptionType),
    riskLevel: toRisk(d.riskLevel),
    status: toStatus(d.status),
    policyId: str(d.policyId) ?? '',
    policyVersionId: str(d.policyVersionId) ?? '',
    policyVersionNumber: num(d.policyVersionNumber) ?? 0,
    policyTitle: str(d.policyTitle) ?? '',
    justification: str(d.justification) ?? '',
    mitigatingControls: strs(d.mitigatingControls),
    impactAssessment: str(d.impactAssessment),
    affectedDepartmentIds: strs(d.affectedDepartmentIds),
    requestorUid: str(d.requestorUid) ?? '',
    requestorEmail: str(d.requestorEmail),
    requestorDisplayName: str(d.requestorDisplayName),
    approvalChain: Array.isArray(d.approvalChain)
      ? (d.approvalChain as Record<string, unknown>[]).map(toStep)
      : [],
    currentApprovalStep: num(d.currentApprovalStep) ?? 0,
    effectiveDate: str(d.effectiveDate),
    expirationDate: str(d.expirationDate),
    reviewIntervalDays: num(d.reviewIntervalDays),
    nextReviewDate: str(d.nextReviewDate),
    policyVersionAtApproval: num(d.policyVersionAtApproval),
    policyVersionChanged: d.policyVersionChanged === true,
    renewalCount: num(d.renewalCount) ?? 0,
    maxRenewals: num(d.maxRenewals),
    conditions: str(d.conditions),
    reasonCategory: toReason(d.reasonCategory),
    oversightCommittee: str(d.oversightCommittee),
    version: num(d.version) ?? 1,
    createdAt: str(d.createdAt),
    updatedAt: str(d.updatedAt),
  };
}

export const exceptionsKey = (orgId: string | null) => ['exceptions', orgId] as const;
export const exceptionKey = (orgId: string | null, id: string | null) => ['exception', orgId, id] as const;

export function useExceptions(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: exceptionsKey(orgId),
    enabled: Boolean(orgId) && enabled,
    queryFn: async () => {
      const res = await call<Record<string, never>, { exceptions: Record<string, unknown>[] }>('listExceptions', {});
      return (res.exceptions ?? []).map(toRecord);
    },
  });
}

export interface ExceptionDetailResult {
  exception: ExceptionRecord;
  versions: ExceptionVersion[];
  events: ExceptionEvent[];
  reviews: ExceptionReview[];
}

export function useExceptionDetail(orgId: string | null, id: string | null, enabled = true) {
  return useQuery({
    queryKey: exceptionKey(orgId, id),
    enabled: Boolean(orgId && id) && enabled,
    queryFn: async (): Promise<ExceptionDetailResult> => {
      const res = await call<{ exceptionId: string }, {
        exception: Record<string, unknown>;
        versions: Record<string, unknown>[];
        events: Record<string, unknown>[];
        reviews: Record<string, unknown>[];
      }>('getExceptionDetail', { exceptionId: id! });
      return {
        exception: toRecord(res.exception),
        versions: (res.versions ?? []).map((v) => ({
          id: String(v.id ?? ''), version: num(v.version) ?? 0, trigger: str(v.trigger) ?? '', createdAt: str(v.createdAt),
        })),
        events: (res.events ?? []).map((e) => ({
          id: String(e.id ?? ''), action: str(e.action) ?? '', actorEmail: str(e.actorEmail),
          details: typeof e.details === 'object' && e.details !== null ? (e.details as Record<string, unknown>) : {},
          createdAt: str(e.createdAt),
        })),
        reviews: (res.reviews ?? []).map((r) => ({
          id: String(r.id ?? ''), reviewerEmail: str(r.reviewerEmail), reviewDate: str(r.reviewDate),
          outcome: toOutcome(r.outcome), riskLevelAtReview: toRisk(r.riskLevelAtReview),
          riskLevelChanged: r.riskLevelChanged === true,
          newRiskLevel: r.newRiskLevel ? toRisk(r.newRiskLevel) : null,
          findings: str(r.findings), conditions: str(r.conditions),
        })),
      };
    },
  });
}

/**
 * The version a policy is currently at, for the exception's reference.
 *
 * Read from the policy document (members may read a published policy):
 * `currentVersionId` and `currentVersionNumber` are what the published
 * version is; a policy that has never been published has neither, and an
 * exception cannot be raised against it.
 */
export interface PolicyVersionRef {
  versionId: string | null;
  versionNumber: number | null;
  title: string;
}

export function usePolicyVersionRef(orgId: string | null, policyId: string) {
  return useQuery({
    queryKey: ['policy-version-ref', orgId, policyId],
    enabled: Boolean(orgId && policyId),
    queryFn: async (): Promise<PolicyVersionRef> => {
      const snap = await getDoc(doc(getFirestore(app), 'orgs', orgId!, 'policies', policyId));
      const d = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
      return {
        versionId: str(d.currentVersionId),
        versionNumber: num(d.currentVersionNumber),
        title: str(d.title) ?? '',
      };
    },
  });
}

/** What create and update are sent. Unset optionals are left out — the
 *  callable SDK would send undefined as null, which the schema refuses. */
export function toPayload(f: ExceptionFields): Record<string, unknown> {
  const days = f.reviewIntervalDays.trim() === '' ? undefined : Number(f.reviewIntervalDays);
  const renewals = f.maxRenewals.trim() === '' ? undefined : Number(f.maxRenewals);
  const p: Record<string, unknown> = {
    title: f.title.trim(),
    exceptionType: f.exceptionType,
    riskLevel: f.riskLevel,
    policyId: f.policyId,
    policyVersionId: f.policyVersionId,
    policyVersionNumber: f.policyVersionNumber ?? undefined,
    policyTitle: f.policyTitle.trim(),
    description: f.description.trim(),
    justification: f.justification.trim(),
    mitigatingControls: f.mitigatingControls,
    impactAssessment: f.impactAssessment.trim(),
    conditions: f.conditions.trim(),
    affectedDepartmentIds: f.affectedDepartmentIds,
    expirationDate: toIsoDateTime(f.expirationDate),
    reviewIntervalDays: Number.isInteger(days) ? days : undefined,
    maxRenewals: Number.isInteger(renewals) ? renewals : undefined,
    reasonCategory: f.reasonCategory,
    oversightCommittee: f.oversightCommittee.trim(),
  };
  for (const k of Object.keys(p)) if (p[k] === undefined) delete p[k];
  return p;
}

export function useCreateException(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (f: ExceptionFields) => {
      const res = await call<unknown, { exceptionId: string }>('createException', toPayload(f));
      return res.exceptionId;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: exceptionsKey(orgId) }),
  });
}

export function useUpdateException(orgId: string | null, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (f: ExceptionFields) => {
      await call<unknown, { success: boolean }>('updateException', { exceptionId: id, ...toPayload(f) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: exceptionsKey(orgId) });
      void qc.invalidateQueries({ queryKey: exceptionKey(orgId, id) });
    },
  });
}

/** A step of a chain as the submit and renewal callables want it. */
export interface ChainStepInput {
  approverUid: string;
  approverEmail?: string;
  approverDisplayName?: string;
  role: ChainRole;
}

export type ExceptionAction =
  | { name: 'submitExceptionForApproval'; approvalChain: ChainStepInput[] }
  | { name: 'recallException' }
  | { name: 'approveExceptionStep'; comments: string }
  | { name: 'rejectExceptionStep'; comments: string }
  | { name: 'activateException' }
  | { name: 'revokeException' }
  | { name: 'initiateExceptionReview' }
  | { name: 'completeExceptionReview'; outcome: ReviewOutcome; findings: string; conditions: string; newRiskLevel: RiskLevel | null }
  | { name: 'requestExceptionRenewal' }
  | { name: 'submitExceptionRenewal'; approvalChain: ChainStepInput[]; expirationDate: string }
  | { name: 'acknowledgeVersionMismatch'; comments: string }
  | { name: 'deleteException' };

/** The payload for one action, with empty optionals left out. */
export function actionPayload(id: string, a: ExceptionAction): Record<string, unknown> {
  const p: Record<string, unknown> = { exceptionId: id };
  const text = (k: string, v: string) => { if (v.trim()) p[k] = v.trim(); };
  switch (a.name) {
    case 'submitExceptionForApproval':
      p.approvalChain = a.approvalChain.map((s, i) => ({ ...s, stepOrder: i }));
      break;
    case 'approveExceptionStep':
    case 'rejectExceptionStep':
    case 'acknowledgeVersionMismatch':
      text('comments', a.comments);
      break;
    case 'completeExceptionReview':
      p.outcome = a.outcome;
      text('findings', a.findings);
      text('conditions', a.conditions);
      p.riskLevelChanged = a.newRiskLevel !== null;
      if (a.newRiskLevel) p.newRiskLevel = a.newRiskLevel;
      break;
    case 'submitExceptionRenewal':
      p.approvalChain = a.approvalChain.map((s, i) => ({ ...s, stepOrder: i }));
      p.expirationDate = toIsoDateTime(a.expirationDate);
      break;
    default:
      break;
  }
  return p;
}

export function useExceptionAction(orgId: string | null, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: ExceptionAction) => {
      await call<unknown, unknown>(a.name, actionPayload(id, a));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: exceptionsKey(orgId) });
      void qc.invalidateQueries({ queryKey: exceptionKey(orgId, id) });
    },
  });
}
